import React, { useState, useCallback, useEffect } from "react";
import { X } from "lucide-react";
import { useIntl } from "react-intl";
import ColorPicker from "../../../../../../library/UI/ColorPicker";
import {
  CANDLE_DEFAULT_UP_COLOR,
  CANDLE_DEFAULT_DOWN_COLOR,
  SHADOW_STYLE_DEFAULT,
} from "../../constants";
import ShadowStyleSelect from "./ShadowStyleSelect";
import "./CandleColorsModal.sass";

/**
 * Modal for customising candle up/down colours and the shadow style (E18).
 * Picked values are previewed on the chart right away (via onPreview);
 * they are saved only on Apply. «Use default colors» saves the default
 * colours and the Regular shadow style and closes the modal. Closing
 * without saving drops the preview.
 *
 * @param {object}   props
 * @param {boolean}  props.isOpen
 * @param {Function} props.onClose
 * @param {Function} props.onPreview - ({ candleUpColor, candleDownColor, shadowStyle }) => void
 * @param {object}   props.chartSettingsApi - { settings, updateSettings }
 */
const CandleColorsModal = ({ isOpen, onClose, onPreview, chartSettingsApi }) => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const { settings, updateSettings } = chartSettingsApi;

  const [upColor, setUpColor] = useState(settings.candleUpColor);
  const [downColor, setDownColor] = useState(settings.candleDownColor);
  const [shadowStyle, setShadowStyle] = useState(settings.shadowStyle);

  // Sync with external settings when modal opens
  useEffect(() => {
    if (isOpen) {
      setUpColor(settings.candleUpColor);
      setDownColor(settings.candleDownColor);
      setShadowStyle(settings.shadowStyle);
    }
  }, [isOpen, settings.candleUpColor, settings.candleDownColor, settings.shadowStyle]);

  // Live preview on the chart while picking
  useEffect(() => {
    if (isOpen) {
      onPreview({ candleUpColor: upColor, candleDownColor: downColor, shadowStyle });
    }
  }, [isOpen, upColor, downColor, shadowStyle, onPreview]);

  const handleApply = useCallback(() => {
    updateSettings({ candleUpColor: upColor, candleDownColor: downColor, shadowStyle });
    onClose();
  }, [upColor, downColor, shadowStyle, updateSettings, onClose]);

  const handleUseDefault = useCallback(() => {
    updateSettings({
      candleUpColor: CANDLE_DEFAULT_UP_COLOR,
      candleDownColor: CANDLE_DEFAULT_DOWN_COLOR,
      shadowStyle: SHADOW_STYLE_DEFAULT,
    });
    onClose();
  }, [updateSettings, onClose]);

  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  if (!isOpen) return null;

  return (
    <div
      className="candle-colors-modal__backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={t("futures.chart.candle_colors.title")}
    >
      <div className="candle-colors-modal">
        <div className="candle-colors-modal__header">
          <h2 className="candle-colors-modal__title">
            {t("futures.chart.candle_colors.title")}
          </h2>
          <button
            type="button"
            className="candle-colors-modal__close"
            aria-label={t("futures.chart.candle_colors.close")}
            onClick={onClose}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="candle-colors-modal__section">
          <p className="candle-colors-modal__section-label">
            {t("futures.chart.candle_colors.uptrend")}
          </p>
          <div className="candle-colors-modal__picker-wrap">
            <ColorPicker value={upColor} onChange={setUpColor} />
          </div>
        </div>

        <div className="candle-colors-modal__section">
          <p className="candle-colors-modal__section-label">
            {t("futures.chart.candle_colors.downtrend")}
          </p>
          <div className="candle-colors-modal__picker-wrap">
            <ColorPicker value={downColor} onChange={setDownColor} />
          </div>
        </div>

        <ShadowStyleSelect value={shadowStyle} onChange={setShadowStyle} />

        <div className="candle-colors-modal__actions">
          <button
            type="button"
            className="candle-colors-modal__btn candle-colors-modal__btn--default"
            onClick={handleUseDefault}
          >
            {t("futures.chart.candle_colors.use_default")}
          </button>
          <button
            type="button"
            className="candle-colors-modal__btn candle-colors-modal__btn--apply"
            onClick={handleApply}
          >
            {t("futures.chart.candle_colors.apply")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CandleColorsModal;
